import {Badge} from "components/UIComponents/Badge";
import React, {ReactElement} from "react";
import {FormattedMessage} from "react-intl";
import {EditorGroup, EditorGroupTitle} from "../EditorGroup";
import {append, assoc, includes} from "ramda";
import {menuEditorActions} from "../../editorSlice";
import {WineBadge, WineItem} from "../../MenuDocumentModel";
import {EditorItem} from "../../editorUtils";
import {useAppDispatch} from "lib/redux/hooks";

export interface BadgeEditorProps {
    wine: EditorItem<WineItem>
}

export function BadgeEditor(props: BadgeEditorProps): ReactElement {
    const dispatch = useAppDispatch();
    const itemKey = props.wine.itemKey;
    const wineItem = props.wine.menuItem;

    function updateProperty<K extends string, T>(property: K, value: T) {
        dispatch(menuEditorActions.editItem({
            key: itemKey,
            newItem: assoc<T, WineItem, K>(property, value, wineItem) as unknown as WineItem
        }));
    }
    
    const badgeActive = (badgeType: WineBadge) => includes(badgeType, wineItem.badges ?? []);
    const setBadgeActive = (badgeType: WineBadge, active: boolean) => {
        const badges = wineItem.badges ?? [];
        const newBadges = active ? append(badgeType, badges) : badges.filter(f => f !== badgeType);
        updateProperty('badges', newBadges);
    }
    
    return (
        <EditorGroup>
            <EditorGroupTitle>
                <FormattedMessage id="editor.wine.preferences"/>
            </EditorGroupTitle>

            <div className="flex flex-wrap items-center gap-2">
                <BadgeChooser
                    type="housewine"
                    active={badgeActive('housewine')}
                    onChange={setBadgeActive}
                />
                <BadgeChooser
                    type="seasonal"
                    active={badgeActive('seasonal')}
                    onChange={setBadgeActive}
                />
                <BadgeChooser
                    type="bio"
                    active={badgeActive('bio')}
                    onChange={setBadgeActive}
                />
                <BadgeChooser
                    type="bestSeller"
                    active={badgeActive('bestSeller')}
                    onChange={setBadgeActive}
                />
                <BadgeChooser
                    type="wineOfMonth"
                    active={badgeActive('wineOfMonth')}
                    onChange={setBadgeActive}
                />
            </div>
        </EditorGroup>
    );
}

interface BadgeChooserProps {
    type: WineBadge
    active: boolean
    onChange: (type: WineBadge, active: boolean) => void
}

function BadgeChooser(props: BadgeChooserProps): ReactElement {
    return (
        <button className="whitespace-nowrap" onClick={() => props.onChange(props.type, !props.active)}>
            {props.active ? (
                <Badge type={props.type}/>
            ) : (
                <span className={`leading-snug rounded py-1 px-2 font-barlow font-medium text-text border
                                  ${props.type === 'seasonal' ? 'border-[#CCB475]' : ''}
                                  ${props.type === 'bio' ? 'border-[#1E7200]' : ''}
                                  ${props.type === 'bestSeller' ? 'border-secondary' : ''}
                                  ${props.type === 'housewine' ? 'border-[#925555]' : ''}
                `}>
                    <FormattedMessage id={`badge.${props.type}`}/>
                </span>
            )}
        </button>
    );
}
